import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, ShoppingCart, Check, Home as HomeIcon, Clock } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const LabPackageDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/lab-packages/${slug}`).then((r) => setP(r.data)).catch(() => setP(null)).finally(() => setLoading(false));
    }, [slug]);

    const addAndGo = () => {
        if (!user) { toast.error("Please sign in"); navigate("/login"); return; }
        const existing = JSON.parse(sessionStorage.getItem("lab-cart") || "[]");
        const key = `pkg-${p.id}`;
        const next = existing.some((x) => x.key === key)
            ? existing
            : [...existing, { key, package_id: p.id, name: p.name, price: p.selling_price }];
        sessionStorage.setItem("lab-cart", JSON.stringify(next));
        navigate("/lab-tests/book");
    };

    if (loading) return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Loading…</main></div>);
    if (!p) return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Package not found. <Link to="/lab-tests" className="text-brand-700 hover:underline">Browse packages</Link></main></div>);

    const savings = Math.max(0, Number(p.mrp || 0) - Number(p.selling_price || 0));
    const savePct = p.mrp > 0 ? Math.round((savings * 100) / p.mrp) : 0;

    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
            <Navbar />
            <main className="mx-auto max-w-4xl px-6 py-10 lg:px-12" data-testid="lab-package-detail">
                <Link to="/lab-tests" className="text-xs text-zinc-500 hover:underline">← All packages</Link>
                <div className="mt-3 grid gap-8 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                        <p className="label-uppercase text-zinc-500">Health package</p>
                        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50" style={{ letterSpacing: "-0.01em" }}>{p.name}</h1>
                        <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300">{p.description}</p>

                        {Array.isArray(p.included_tests) && p.included_tests.length > 0 && (
                            <section className="mt-8">
                                <p className="label-uppercase text-zinc-500">Includes {p.included_tests.length} tests</p>
                                <ul className="mt-3 grid gap-1.5 sm:grid-cols-2" data-testid="lab-package-included-tests">
                                    {p.included_tests.map((it) => (
                                        <li key={it.id} className="flex items-start gap-2 rounded-md border border-zinc-100 p-2 text-sm dark:border-white/10">
                                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                            <div className="min-w-0">
                                                <p className="font-medium text-zinc-900 dark:text-zinc-50">{it.name}</p>
                                                <p className="text-[11px] text-zinc-500">{it.category}{it.reporting_time ? ` • ${it.reporting_time}` : ""}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        <div className="mt-8 grid gap-3 sm:grid-cols-2">
                            <div className="rounded-2xl border border-zinc-100 p-4 dark:border-white/10">
                                <p className="flex items-center gap-1.5 text-xs font-medium text-zinc-500"><HomeIcon className="h-3.5 w-3.5 text-emerald-600" /> Sample collection</p>
                                <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-50">Free home pickup</p>
                            </div>
                            <div className="rounded-2xl border border-zinc-100 p-4 dark:border-white/10">
                                <p className="flex items-center gap-1.5 text-xs font-medium text-zinc-500"><Clock className="h-3.5 w-3.5" /> Reports</p>
                                <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-50">24–48 hours</p>
                            </div>
                        </div>
                    </div>

                    <aside className="space-y-4">
                        <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-white p-5 dark:from-emerald-950/20 dark:to-zinc-900 dark:border-white/10">
                            <Sparkles className="h-5 w-5 text-emerald-700" />
                            <p className="mt-2">
                                <span className="mr-2 text-sm line-through text-zinc-400">{inr(p.mrp)}</span>
                                <span className="text-2xl font-semibold text-zinc-900 dark:text-zinc-50">{inr(p.selling_price)}</span>
                            </p>
                            {savings > 0 && <p className="text-xs font-medium text-emerald-700">Save {inr(savings)} ({savePct}%)</p>}
                            <button onClick={addAndGo} className="btn-brand mt-4 w-full" data-testid="lab-package-detail-book">
                                <ShoppingCart className="mr-1.5 inline h-4 w-4" /> Book this package
                            </button>
                            <p className="mt-2 text-[11px] text-zinc-500">Free home collection • Reports within 48 hrs.</p>
                        </div>
                    </aside>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default LabPackageDetail;
